import {
	Avatar,
	Card,
	Notification,
	H2,
	H3,
	H4,
	Icon,
	T,
	v,
	Image,
	Spinner,
	SpinnerCircle,
	Divider,
	H5,
	Button,
	ButtonIcon,
	styled,
	H6,
	th,
	Link,
	Input,
	LineButton,
	Box,
	P,
	useLinks,
	mediaUp,
	mediaDown,
	useToast,
} from "@veracity/vui";
import { Observer } from "mobx-react-lite";
import React, { useCallback, useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { identityClient } from "..";
import { Label } from "../components/Label";
import { LeftContainer } from "../components/LeftContainer";
import { Page } from "../components/Page";
import { UserItemRefAvatar } from "../components/UserItemRefAvatar";
import { CompanyRefDto } from "../models/auto-generated/CompanyRefDto";
import { UserItemRef } from "../models/UserItemRef";
import { OVERVIEW_ROUTE, ROUTES } from "../routes";
import { CancellationToken } from "../utils/CancellationToken";
import { delayAsync } from "../utils/delayAsync";
import { Formik } from "formik";
import * as yup from "yup";
import * as libphonenumber from "libphonenumber-js";
import { stringIsEmpty } from "../utils/stringIsEmpty";
import { Ids } from "../components/Ids";
import { InputHelpText } from "../components/InputHelpText";
import { CountrySelector } from "../components/CountrySelector";
import { CountryDto } from "../models/auto-generated/CountryDto";
import { CountryCallingCodeSelector } from "../components/CountryCallingCodeSelector";
import { fetchVeracityVoid } from "../services/fetchVeracity";
import { UpdateUserWithMobileDto } from "../models/auto-generated/UpdateUserWithMobileDto";
import { showErrorDialogAsync } from "../showErrorDialogAsync";
import { computed } from "mobx";
import { ENFORCE_MFA_PATH } from "../misc/auto-generated/constants";
import { showPendingDialogAsync } from "../showPendingDialogAsync";
import { showChangePictureDialogAsync } from "../showChangePictureDialogAsync";
import { useDropzone } from "react-dropzone";
import { UserProfile } from "../models/UserProfile";
import { UserOptInState } from "../models/UserOptInState";
import { profile } from "console";
import { showAskQuestionDialogAsync } from "../showAskQuestionDialogAsync";
import { OperationCancelledError } from "../utils/OperationCancelledError";
import { showOkCancelDialogAsync } from "../showOkCancelDialogAsync";
import { DirectoryUsers } from "../services/DirectoryUsers";
import { showVerifyPhoneNumberDialogAsync } from "../showVerifyPhoneNumberDialogAsync";
import { defaultGetInitials } from "../utils/defaultGetInitials";

// https://www.figma.com/file/8LOpBedHx2JODBsttyzoKh/Profile---to-fix?node-id=4373%3A598
// https://dnvgl-one.visualstudio.com/Veracity/_workitems/edit/333241

export function UserProfilePage() {
	let [state, setState] = useState<{
		isLoading: boolean;
		threw?: boolean;
		error?;
	}>({ isLoading: true });

	if (state.threw) throw state.error;

	useEffect(() => {
		let ct = new CancellationToken();

    (async () => {
      try {
        await Promise.all([
          identityClient.fetchIsLoggedInWithMfaAsync(),
          identityClient.fetchSortedCountriesAsync(),
          (async () => {
            try {
              await identityClient.clearLoggedInUserProfileApiCacheAsync();
            } catch (e) {
              console.error(e);
            }
            await identityClient.refreshLoggedInUserProfileAsync();
            await identityClient.fetchAffiliatedCompaniesAsync();
            await identityClient.fetchAvailableCompaniesAsync();
          })(),
        ]);

        ct.throwIfCancellationRequested();

				// A small hack to ensure that the UserProfile.country property is able to initialize immediately before feeding it into Formik's initialValues
				identityClient.lazyCountriesByCountryCode;
				await new Promise<void>((r) => queueMicrotask(r));
				ct.throwIfCancellationRequested();
			} catch (e) {
				if (ct.isCancellationRequested) return;

				if (IS_DEVELOPMENT) console.debug(e);

				setState({
					isLoading: false,
					threw: true,
					error: e,
				});
			}

			setState({
				isLoading: false,
			});
		})();

		return ct.cancel.bind(ct);
	}, []);

	if (state.isLoading)
		return (
			<Page key="LOADING_USER_PROFILE" center>
				<Spinner />
			</Page>
		);

	return (
		<Page>
			<LeftContainer
				display="flex"
				flexDirection="column"
				alignItems="center"
			>
				<Picture />
				<H3 mt={2} mb={{ xs: 30 / 8, md: 40 / 8 }}>
					<Observer>
						{() =>
							identityClient.loggedInUserProfile?.dto
								.DisplayName || (null as any)
						}
					</Observer>
				</H3>
				<Observer>
					{() => {
						let loggedInUserProfile =
							identityClient.loggedInUserProfile;

						if (!loggedInUserProfile) return null;

						let dto = loggedInUserProfile.dto;

						if (loggedInUserProfile.canUpdateProfileInformation) {
							if (dto.VerifiedPhone)
								return (
									<EditPersonalInformationCard
										canEditMobile={false}
									/>
								);

							return (
								<EditPersonalInformationCard
									canEditMobile={true}
								/>
							);
						}

						return (
							<>
								<Observer>
									{() => {
										if (
											stringIsEmpty(
												loggedInUserProfile.dto
													.EditProfileUrl
											)
										)
											return (
												<Notification
													icon="falInfoCircle"
													text="Please access your company webpage to edit your profile"
													w="100%"
													mb={20 / 8}
												/>
											);
										return null;
									}}
								</Observer>
								<NonEditablePersonalInformationCard />
							</>
						);
					}}
				</Observer>
				<Observer>
					{() => {
						let loggedInUserProfile =
							identityClient.loggedInUserProfile;

						if (!loggedInUserProfile) return null;

						return loggedInUserProfile.canChangeEmail ? (
							<EditEmailCard
								userProfile={identityClient.loggedInUserProfile}
								showTitle
							/>
						) : (
							<NonEditableEmailCard />
						);
					}}
				</Observer>
				<AffiliatedCompaniesCard />
			</LeftContainer>
		</Page>
	);
}

function Picture() {
	let onDrop = useCallback((acceptedFiles: File[]) => {
		if (acceptedFiles?.length)
			showChangePictureDialogAsync(acceptedFiles[0]);
	}, []);

	let { getRootProps, getInputProps } = useDropzone({
		onDrop,
		accept: { "image/*": [] },
		multiple: false,
	});

	return (
		<Avatar
			w="116px"
			h="116px"
			fontSize="lg"
			{...getRootProps()}
			title="Change picture"
		>
			<input {...getInputProps()} />
			<PictureInnerDiv>
				<Observer>
					{() => {
						let loggedInUserProfile =
							identityClient.loggedInUserProfile;
						if (!loggedInUserProfile) return null;
						let pictureUrlState = loggedInUserProfile.pictureUrl;
						if (pictureUrlState.isExecuting)
							return (
								<SpinnerCircle
									animation={`vui-spin .9s linear infinite`}
									className="vui-spinnerCircle"
									w="96px"
									h="96px"
									borderWidth={5}
									borderColor="blue.20"
									borderTopColor="blue.50"
								/>
							);
						if (pictureUrlState.result)
							return (
								<Image
									src={pictureUrlState.result}
									w="96px"
									h="96px"
									animation="fadeIn"
								/>
							);
						return defaultGetInitials(
							loggedInUserProfile.dto.DisplayName
						) as any;
					}}
				</Observer>
			</PictureInnerDiv>
		</Avatar>
	);
}

const PictureInnerDiv = styled.divBox`
	border-radius: 50%;
	overflow: hidden;
	position: relative;
	user-select: none;
`;

// https://www.figma.com/file/8LOpBedHx2JODBsttyzoKh/Profile---to-fix?node-id=4373%3A598
function NonEditablePersonalInformationCard() {
	return (
		<Card
			w="100%"
			px={{ xs: 2, md: 4 }}
			py={{ xs: 2.5, md: 28 / 8 }}
			column
			shadow={0}
			border="1px solid grey.30"
		>
			<v.div display="flex" alignItems="center">
				<H5 mr={1}>Personal information</H5>
				<Observer>
					{() => {
						let editProfileUrl =
							identityClient.loggedInUserProfile?.dto
								.EditProfileUrl;

						if (stringIsEmpty(editProfileUrl)) return null;

						return (
							<Button
								as="a"
								href={editProfileUrl}
								target="_blank"
								ml="auto"
								variant="secondaryDark"
								colorScheme="blue"
								iconRight={
									<ButtonIcon
										ml={1}
										name="falExternalLink"
										size="sm"
									/>
								}
							>
								Edit
							</Button>
						);
					}}
				</Observer>
			</v.div>
			<Divider mt={11 / 8} />
			<Label mt={3}>Name</Label>
			<T mt={1}>
				<Observer>
					{() => {
						let dto = identityClient.loggedInUserProfile.dto;
						return [dto.FirstName || "", dto.LastName || ""]
							.map((p) => p.trim())
							.join(" ") as any;
					}}
				</Observer>
			</T>
			<Label mt={3}>Country</Label>
			<T mt={1}>
				<Observer>
					{() => {
						let loggedInUserProfile =
							identityClient.loggedInUserProfile;
						if (!loggedInUserProfile) return null;
						let country = loggedInUserProfile.country;
						if (country) return country.Name || (null as any);
						let dto = loggedInUserProfile.dto;
						if (dto.Country) {
							let countriesByCountryCode =
								identityClient.lazyCountriesByCountryCode.state;
							if (countriesByCountryCode.isExecuting)
								return "\u00A0";
							return dto.Country;
						}
						return "N/A" as any;
					}}
				</Observer>
			</T>
			<Label mt={3}>Phone number</Label>
			<T mt={1}>
				<Observer>
					{() =>
						(
							identityClient.loggedInUserProfile?.dto.Mobile || ""
						).trim() || ("N/A" as any)
					}
				</Observer>
			</T>
		</Card>
	);
}

function EditPersonalInformationCard(props: { canEditMobile: boolean }) {
	let { showSuccess } = useToast();

	return (
		<Card
			w="100%"
			px={{ xs: 2, md: 4 }}
			py={{ xs: 2.5, md: 28 / 8 }}
			column
			shadow={0}
			border="1px solid grey.30"
		>
			<H5>Personal information</H5>
			<Divider mt={11 / 8} />
			<Formik
				initialValues={generateInitialValuesForPersonalInformationSchema()}
				validationSchema={personalInformationSchema}
				onSubmit={async (values, { setFieldError, resetForm }) => {
					try {
						let loggedInUserProfile =
							identityClient.loggedInUserProfile;

						if (props.canEditMobile) {
							let mobile = values.nationalPhoneNumber?.trim();

							if (mobile)
								mobile =
									"+" +
									values.phoneNumberCountry.CallingCode +
									mobile;

							if (stringIsEmpty(mobile)) {
								let allAvailableOptIns =
									await identityClient.getAllAvailableOptInsAsync(
										"security"
									);

								if (allAvailableOptIns) {
									for (let o of allAvailableOptIns) {
										if (
											o.id === "security.mfa" &&
											o.displayEnabledWithPendingTakenIntoAccount
										) {
											try {
												await showOkCancelDialogAsync(
													"Confirm changes",
													"Because you have previously enabled two-factor authentication and because the phone number is about to be removed, two-factor authentication will become disabled."
												);
											} catch (e) {
												if (
													e instanceof
													OperationCancelledError
												)
													return;

												throw e;
											}

											try {
												await o.displayEnableDisableAsync(
													false
												);
											} catch (e) {
												showErrorDialogAsync(
													"Error when disabling two-factor authentication",
													e
												);
												return;
											}

											break;
										}
									}
								}
							} else if (
								mobile !==
									loggedInUserProfile.dto.Mobile?.trim() &&
								loggedInUserProfile.dto.VerifiedPhone
							) {
								try {
									await showOkCancelDialogAsync(
										"Confirm changes",
										"Because you have previously verified your phone number, you will need to confirm the new phone number."
									);
								} catch (e) {
									if (e instanceof OperationCancelledError)
										return;

									throw e;
								}

                // try {
                // 	await showPendingDialogAsync(DirectoryUsers.updateCurrentUsersPhone({ OldAddress: loggedInUserProfile.dto.Mobile, NewAddress: mobile }), "Please wait...");
                // }

                // catch (e) {
                // 	showErrorDialogAsync("Error when requesting SMS verification code", e);
                // 	return;
                // }

								try {
									await showVerifyPhoneNumberDialogAsync(
										mobile
									);
								} catch (e) {
									if (e instanceof OperationCancelledError)
										return;

									throw e;
								}
							}

							await loggedInUserProfile.updateProfileInformationWithMobile(
								{
									FirstName: values.firstName,
									LastName: values.lastName,
									DisplayName: values.displayName,
									Company: values.company,
									Mobile: mobile,
									Country: values.country.Code,
								}
							);
						} else
							await loggedInUserProfile.updateProfileInformationWithoutMobile(
								{
									FirstName: values.firstName,
									LastName: values.lastName,
									DisplayName: values.displayName,
									Company: values.company,
									Country: values.country.Code,
								}
							);
					} catch (e) {
						showErrorDialogAsync("Error when saving profile", e);
						return;
					}

					showSuccess("Successfully saved the personal information");
					resetForm({
						values: generateInitialValuesForPersonalInformationSchema(),
					});
				}}
			>
				{({
					values,
					errors,
					touched,
					isSubmitting,
					handleSubmit,
					getFieldProps,
					setFieldValue,
					resetForm,
				}) => {
					return (
						<form noValidate onSubmit={handleSubmit}>
							<v.fieldset disabled={isSubmitting} p={0}>
								<Ids
									length={2}
									fn={(ids) => (
										<>
											<Label mt={3} htmlFor={ids[0]}>
												First name
											</Label>
											<Input
												type="email"
												mt={2}
												maxW={{ md: "320px" }}
												id={ids[0]}
												state={
													touched.firstName
														? errors.firstName
															? "error"
															: "success"
														: null
												}
												variant={
													touched.firstName
														? errors.firstName
															? "red"
															: "green"
														: null
												}
												inputProps={{
													autoComplete: "given-name",
													"aria-invalid": !!(
														touched.firstName &&
														errors.firstName
													),
													"aria-errormessage":
														touched.firstName &&
														errors.firstName
															? ids[1]
															: "",
												}}
												{...getFieldProps("firstName")}
											/>
											{touched.firstName &&
												errors.firstName && (
													<InputHelpText
														id={ids[1]}
														color="red.80"
													>
														{errors.firstName}
													</InputHelpText>
												)}
										</>
									)}
								/>
								<Ids
									length={2}
									fn={(ids) => (
										<>
											<Label mt={3} htmlFor={ids[0]}>
												Last name
											</Label>
											<Input
												type="email"
												mt={2}
												maxW={{ md: "320px" }}
												id={ids[0]}
												state={
													touched.lastName
														? errors.lastName
															? "error"
															: "success"
														: null
												}
												variant={
													touched.lastName
														? errors.lastName
															? "red"
															: "green"
														: null
												}
												inputProps={{
													autoComplete: "family-name",
													"aria-invalid": !!(
														touched.lastName &&
														errors.lastName
													),
													"aria-errormessage":
														touched.lastName &&
														errors.lastName
															? ids[1]
															: "",
												}}
												{...getFieldProps("lastName")}
											/>
											{touched.lastName &&
												errors.lastName && (
													<InputHelpText
														id={ids[1]}
														color="red.80"
													>
														{errors.lastName}
													</InputHelpText>
												)}
										</>
									)}
								/>
								<Ids
									length={2}
									fn={(ids) => (
										<>
											<Label mt={3} htmlFor={ids[0]}>
												Display name
											</Label>
											<Input
												type="email"
												mt={2}
												maxW={{ md: "320px" }}
												id={ids[0]}
												state={
													touched.displayName
														? errors.displayName
															? "error"
															: "success"
														: null
												}
												variant={
													touched.displayName
														? errors.displayName
															? "red"
															: "green"
														: null
												}
												inputProps={{
													"aria-invalid": !!(
														touched.displayName &&
														errors.displayName
													),
													"aria-errormessage":
														touched.displayName &&
														errors.displayName
															? ids[1]
															: "",
												}}
												{...getFieldProps(
													"displayName"
												)}
											/>
											{touched.displayName &&
												errors.displayName && (
													<InputHelpText
														id={ids[1]}
														color="red.80"
													>
														{errors.displayName}
													</InputHelpText>
												)}
										</>
									)}
								/>
								<Ids
									length={2}
									fn={(ids) => (
										<>
											<Label mt={3} htmlFor={ids[0]}>
												Company
											</Label>
											<Input
												type="email"
												mt={2}
												maxW={{ md: "320px" }}
												id={ids[0]}
												state={
													touched.company
														? errors.company
															? "error"
															: "success"
														: null
												}
												variant={
													touched.company
														? errors.company
															? "red"
															: "green"
														: null
												}
												inputProps={{
													autoComplete: "given-company",
													"aria-invalid": !!(
														touched.company &&
														errors.company
													),
													"aria-errormessage":
														touched.company &&
														errors.company
															? ids[1]
															: "",
												}}
												{...getFieldProps("company")}
											/>
											{touched.company &&
												errors.company && (
													<InputHelpText
														id={ids[1]}
														color="red.80"
													>
														{errors.company}
													</InputHelpText>
												)}
										</>
									)}
								/>
								<Ids
									length={2}
									fn={(ids) => (
										<>
											<Label mt={3} htmlFor={ids[0]}>
												Country / region
											</Label>
											<CountrySelector
												required
												buttonProps={{
													id: ids[1],
													mt: 2,
													maxW: { md: "320px" },
												}}
												value={values.country}
												onChange={(c) => {
													setFieldValue("country", c);
												}}
											/>
											{touched.country &&
												errors.country && (
													<InputHelpText
														id={ids[1]}
														color="red.80"
													>
														{errors.country}
													</InputHelpText>
												)}
										</>
									)}
								/>
								<Ids
									length={2}
									fn={(ids) => (
										<v.div mb={3}>
											<Label mt={3} htmlFor={ids[0]}>
												Phone number{" "}
												<T
													color="grey.80"
													fontWeight="regular"
												>
													(optional)
												</T>
											</Label>
											<v.div
												mt={2}
												maxW={{ md: "320px" }}
												display="flex"
											>
												<CountryCallingCodeSelector
													buttonProps={{
														disabled:
															!props.canEditMobile,
													}}
													value={
														values.phoneNumberCountry
													}
													onChange={(c) => {
														setFieldValue(
															"phoneNumberCountry",
															c
														);
													}}
												/>
												<Input
													disabled={
														!props.canEditMobile
													}
													type="tel"
													ml={1}
													flex="1"
													id={ids[0]}
													state={
														touched.nationalPhoneNumber
															? errors.nationalPhoneNumber
																? "error"
																: values.nationalPhoneNumber
																? "success"
																: null
															: null
													}
													variant={
														touched.nationalPhoneNumber
															? errors.nationalPhoneNumber
																? "red"
																: values.nationalPhoneNumber
																? "green"
																: null
															: null
													}
													inputProps={{
														autoComplete:
															"tel-national",
														"aria-invalid": !!(
															touched.nationalPhoneNumber &&
															errors.nationalPhoneNumber
														),
														"aria-errormessage":
															touched.nationalPhoneNumber &&
															errors.nationalPhoneNumber
																? ids[1]
																: "",
													}}
													{...getFieldProps(
														"nationalPhoneNumber"
													)}
												/>
											</v.div>
											{props.canEditMobile &&
												touched.nationalPhoneNumber &&
												errors.nationalPhoneNumber && (
													<InputHelpText
														id={ids[1]}
														color="red.80"
													>
														{
															errors.nationalPhoneNumber
														}
													</InputHelpText>
												)}
											{!props.canEditMobile && (
												<InputHelpText
													id={ids[1]}
													fontSize="sm"
													display="flex"
													mt={8}
												>
													<Icon
														name="falInfoCircle"
														size="sm"
														mr={1}
													/>
													{/* <div>
												If you wish to update your phone number, you must be logged in using two-factor authentication. Please <Link href={ENFORCE_MFA_PATH}>click here</Link> to sign in using two-factor authentication.
											</div> */}
													<div>
														If you wish to update
														your phone number,
														please contact support.
													</div>
												</InputHelpText>
											)}
										</v.div>
									)}
								/>
								<v.div display="flex" mt={3}>
									<Button
										type="submit"
										isLoading={isSubmitting}
									>
										Save
									</Button>
									<LineButton
										ml={1}
										onClick={() =>
											resetForm({
												values: generateInitialValuesForPersonalInformationSchema(),
											})
										}
									>
										Reset
									</LineButton>
								</v.div>
							</v.fieldset>
						</form>
					);
				}}
			</Formik>
		</Card>
	);
}

export const personalInformationSchema = yup.object().shape({
	firstName: yup.string().required("Please enter your first name"),
	lastName: yup.string().required("Please enter your last name"),
	displayName: yup.string().required("Please enter a display name"),
	company: yup.string().required("Please enter your company name"),
	country: yup.object().nullable().required("Please select your country"),
	phoneNumberCountry: yup.object().nullable(),
	nationalPhoneNumber: yup
		.string()
		.optional()
		.test(
			"national-phone-number",
			"Please enter a valid phone number",
			(value, context) => {
				if (!value?.trim()) return true;
				try {
					return libphonenumber
						.parsePhoneNumber(
							value,
							context.parent.phoneNumberCountry?.Code
						)
						.isValid();
				} catch {
					return false;
				}
			}
		),
});

export function generateInitialValuesForPersonalInformationSchema() {
	let loggedInUserProfile = identityClient.loggedInUserProfile;

	if (!loggedInUserProfile) return {};

  let dto = loggedInUserProfile.dto;

	try {
		var parsedPhoneNumber = libphonenumber.parsePhoneNumber(dto.Mobile);

		if (parsedPhoneNumber.country) {
			let countriesByCountryCode =
				identityClient.lazyCountriesByCountryCode.state;
			if (countriesByCountryCode.result)
				var phoneNumberCountry =
					countriesByCountryCode.result[
						parsedPhoneNumber.country.toUpperCase()
					];
		}
	} catch {}

	return {
		firstName: dto.FirstName,
		lastName: dto.LastName,
		displayName: dto.DisplayName,
		company: dto.Company,
		country: loggedInUserProfile.country,
		phoneNumberCountry,
		nationalPhoneNumber: parsedPhoneNumber?.nationalNumber,
	};
}

// https://www.figma.com/file/8LOpBedHx2JODBsttyzoKh/Profile---to-fix?node-id=4373%3A598
function NonEditableEmailCard() {
	return (
		<Card
			w="100%"
			px={{ xs: 2, md: 4 }}
			py={{ xs: 2.5, md: 28 / 8 }}
			mt={3}
			column
			shadow={0}
			border="1px solid grey.30"
		>
			<H5 mr={1}>E-mail address</H5>
			<Divider mt={11 / 8} />
			<T mt={3}>
				<Observer>
					{() =>
						identityClient.loggedInUserProfile?.dto.Email ||
						(null as any)
					}
				</Observer>
			</T>
		</Card>
	);
}

export function EditEmailCard(props: {
	showTitle: boolean;
	userProfile: UserProfile;
}) {
	let { showInfo } = useToast();
	const navigate = useNavigate();

	return (
		<Card
			w="100%"
			px={{ xs: 2, md: 4 }}
			py={{ xs: 2.5, md: 28 / 8 }}
			column
			shadow={0}
			border="1px solid grey.30"
			mt={3}
		>
			{props.showTitle && (
				<>
					<H5>E-mail address</H5>
					<Divider mt={11 / 8} mb={3} />
				</>
			)}
			<Formik
				initialValues={{ newEmailAddress: "" }}
				validationSchema={emailSchema}
				onSubmit={async (values, { setFieldError, resetForm }) => {
					try {
						await props.userProfile.changeEmailAsync(
							values.newEmailAddress
						);
					} catch (e) {
						showErrorDialogAsync(
							"Error when saving e-mail address",
							e
						);
						return;
					}

					let dto = props.userProfile?.dto;

					showInfo(
						"An e-mail containing a verification link has been sent to " +
							(props.userProfile.isLimited
								? dto.AlternateEmail
								: dto.PendingEmail)
					);
					resetForm({ values: { newEmailAddress: "" } });
				}}
			>
				{({
					values,
					errors,
					touched,
					isSubmitting,
					handleSubmit,
					getFieldProps,
					setFieldValue,
					resetForm,
				}) => {
					return (
						<form noValidate onSubmit={handleSubmit}>
							<v.fieldset disabled={isSubmitting} p={0}>
								<Label>Current e-mail address</Label>
								<T>
									<Observer>
										{() =>
											props.userProfile?.dto.Email ||
											(null as any)
										}
									</Observer>
								</T>
								<Ids
									length={2}
									fn={(ids) => (
										<>
											<Label mt={3} htmlFor={ids[0]}>
												New e-mail address
											</Label>
											<Input
												type="email"
												mt={1}
												maxW={{ md: "320px" }}
												id={ids[0]}
												state={
													touched.newEmailAddress
														? errors.newEmailAddress
															? "error"
															: "success"
														: null
												}
												variant={
													touched.newEmailAddress
														? errors.newEmailAddress
															? "red"
															: "green"
														: null
												}
												inputProps={{
													autoComplete: "email",
													"aria-invalid": !!(
														touched.newEmailAddress &&
														errors.newEmailAddress
													),
													"aria-errormessage":
														touched.newEmailAddress &&
														errors.newEmailAddress
															? ids[1]
															: "",
												}}
												{...getFieldProps(
													"newEmailAddress"
												)}
											/>
											{touched.newEmailAddress &&
												errors.newEmailAddress && (
													<InputHelpText
														id={ids[1]}
														color="red.80"
													>
														{errors.newEmailAddress}
													</InputHelpText>
												)}
											<InputHelpText
												id={ids[1]}
												fontSize="sm"
												display="flex"
												mt={2}
											>
												<Icon
													name="falInfoCircle"
													size="sm"
													mr={1}
												/>
												<div>
													<Observer>
														{() =>
															props.userProfile
																?.isLimited
																? "An e-mail containing a verification link will be sent to your e-mail address. Please click the verification link in order to complete the process."
																: ("An e-mail containing a verification link will be sent to the new e-mail address. Please click the verification link in order to complete the process." as any)
														}
													</Observer>
												</div>
											</InputHelpText>
										</>
									)}
								/>
								<v.div display="flex" mt={3}>
									<Button
										type="submit"
										isLoading={isSubmitting}
									>
										Change
									</Button>
									<LineButton
										ml={2}
										onClick={() => resetForm()}
									>
										Clear
									</LineButton>
								</v.div>
								<Observer>
									{() => {
										let dto = props.userProfile?.dto;

										if (dto?.ChangeEmailPending)
											return (
												<Notification
													mt={3}
													icon="falInfoCircle"
													w="100%"
													text={
														"An e-mail containing a verification link has been sent to " +
														(props.userProfile
															.isLimited
															? dto.AlternateEmail
															: dto.PendingEmail)
													}
													action={
														<Notification.Button
															text="Resend"
															title="Resend verification e-mail"
															onClick={async () => {
																try {
																	await showPendingDialogAsync(
																		props.userProfile.changeEmailAsync(
																			dto.PendingEmail
																		),
																		"Resending verification e-mail..."
																	);
																} catch (e) {
																	showErrorDialogAsync(
																		"Error when resending verification e-mail",
																		e
																	);
																	return;
																}

																// TODO: Toast
															}}
														/>
													}
												/>
											);

										return null;
									}}
								</Observer>
							</v.fieldset>
						</form>
					);
				}}
			</Formik>
		</Card>
	);
}

export const emailSchema = yup.object().shape({
	newEmailAddress: yup
		.string()
		.required("Please enter an e-mail address")
		.test(
			"email-address-format",
			"Please enter a valid e-mail address",
			(value, context) => {
				return (async () => {
					return await identityClient.validateEmailAddressFormatAsync(
						value || ""
					);
				})();
			}
		)
		.test(
			"email-address-domain",
			"Email address domain not allowed",
			(value, context) => {
				return (async () => {
					return await identityClient.validateEmailAddressDomainAsync(
						value || ""
					);
				})();
			}
		),
});

const applyToCompany = async (tenantId: string) => {
  return await identityClient.fetchApplyForCompanyResult(tenantId);
};

// https://www.figma.com/file/8LOpBedHx2JODBsttyzoKh/Profile---to-fix?node-id=4373%3A598
function AffiliatedCompaniesCard() {
  let { showSuccess } = useToast();
  const systemTenantId = "00000000-0000-0000-0000-000000000000";
  const filteredCompanies = identityClient.affiliatedCompanies.filter(
    (c) => c.Id != systemTenantId && (c.Administrators?.length ?? 0) != 0
  );

  return (
    <Card
      w="100%"
      px={{ xs: 2, md: 4 }}
      py={{ xs: 2.5, md: 28 / 8 }}
      mt={3}
      column
      shadow={0}
      border="1px solid grey.30"
    >
      <H5 mr={1}>Affiliated companies</H5>
      <Divider mt={11 / 8} />
      <v.div display={{ md: "grid" }} gridTemplateColumns="50% 50%">
        {filteredCompanies &&
          filteredCompanies.map((c) => (
            <AffiliatedCompany key={c.Id}>
              <div>
                <v.div display="flex" pr={1}>
                  <Icon name="falBuilding" color="blue.80" mr={2} />
                  <div>
                    <H5>{c.Name}</H5>
                    <v.div whiteSpace="pre" mt={1}>
                      {[
                        c.AddressLine1,
                        c.AddressLine2,
                        c.AddressLine3,
                        c.AddressLine4,
                      ]
                        .map((x) => x?.trim() || "")
                        .filter((x) => x)
                        .join("\n")}
                    </v.div>
                  </div>
                </v.div>
              </div>
              <div>
                {c.Administrators?.length > 0 && <H6>Company administrators</H6>}
                {c.Administrators?.map((a) => (
                  <Administrator key={a.Id}>
                    <UserItemRefAvatar
                      w="56px"
                      h="56px"
                      userItemRef={UserItemRef.byId.get(a.Id)}
                      spinnerCircleProps={{
                        w: (96 / 116) * 56,
                        h: (96 / 116) * 56,
                        borderWidth: 3,
                      }}
                    />
                    <v.div mt={1}>{a.Name}</v.div>
                    <Link mt={1} href={"mailto:" + a.Email} isUnderlined>
                      {a.Email}
                    </Link>
                    <v.div mt={1}>{a.Phone}</v.div>
                  </Administrator>
                ))}
              </div>
            </AffiliatedCompany>
          ))}
      </v.div>
      <H6 mt={3}>Connect with your company</H6>
      <P mt={1}>
        You may be eligible to connect with companies using the domain{" "}
        <em>
          <Observer>
            {() => {
              let email = identityClient.loggedInUserProfile?.dto.Email;
              if (!email) return null;
              let i = email.indexOf("@");
              if (i === -1) return null;
              return email.substring(i + 1) as any;
            }}
          </Observer>
        </em>
        .
      </P>
      {identityClient.availableCompanies &&
        identityClient.availableCompanies.map((c) => (
          <AffiliatedCompany key={c.TenantId}>
            <div>
              <v.div display="flex" pr={1}>
                <Icon name="falBuilding" color="blue.80" mr={2} />
                <div>
                  <H5>{c.Name}</H5>
                </div>
                <Button
                  ml={2}
                  onClick={() => {
                    applyToCompany(c.TenantId).then((x) =>
                      showSuccess("Successfully applied to company")
                    );
                  }}
                >
                  Apply to join
                </Button>
              </v.div>
            </div>
          </AffiliatedCompany>
        ))}
    </Card>
  );
}

const AffiliatedCompany = styled.div`
	display: contents;

  > * {
    border-bottom: 1px solid ${th.color("grey.30")};
    padding-bottom: ${th.space(3)};
    padding-top: ${th.space(20 / 8)};

    &:first-child {
      ${mediaDown("md")} {
        border-bottom: none;
        padding-bottom: 0;
      }
    }
  }
`;

const Administrator = styled.div`
	margin-top: ${th.space(40 / 8)};

	&:first-of-type {
		margin-top: ${th.space(2)};
	}
`;
